/** @jsx jsx */
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { css, jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import _ from 'lodash'
import { EMPTY_ICON } from '@constants/index'
import { AdvEmpty } from '@components/AdvEmpty'
import { ListView } from 'antd-mobile'
import { FROM_VIEW_TYPE } from '@v2/constants/common'
import { AdvComments } from '@components/AdvComment/index.style'
import { AdvEditor, AdvEditorData } from '@components/AdvEditor'
import { MatchUpDetailSportTalkWrap } from '@views/Matchup/MatchUpDetail/index.style'
import { isMobile } from 'react-device-detect'
import { preventScrollStyle } from '@styles/base.style'
import classNames from 'classnames'
import useRootStore from '@store/useRootStore'
import { MatchTrack, MatchupStore } from '@store/matchupStore'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from '@v2/constants/cheer'
import * as ImageUtils from '@utils/image-utils'
import { SportTalkItem } from 'components/SportTalkItem'
import { AdvTimelineItem } from '@components/SportTalkItem/AdvTimelineItem'
import PageResetIco from '@shared/Icon/EtcIcon/PageResetIco'
import { isNotStartedMatchStatus, matchupDetailScrollToTop } from '@utils/utils'
import { AdvButton } from '@components/Button'
import { CheerForTeamStore } from '@store/cheerForTeamStore'
import { SportIssueList } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/SportIssueList'
import { Schema$SportTalkListItem } from '@interface/cheer-for-team'
import { SPORT_TALK_ITEM_TYPE } from '@v2/constants/sport-issue'

const ListViewStyle = css`
    position: static !important;
    ${preventScrollStyle};

    .am-list-body {
        margin-bottom: 90px;
    }
`

interface CheerForTeamProps {
  isGameInfoMatchChart?: boolean
}

let saveCheerForTeamTimer = null
let refreshSportTalkTimer = null

export const SportTalk: React.FC<CheerForTeamProps> = observer((props) => {

  const { cheerForTeamStore, componentStore, matchupStore, globalStore, chattingStore, authStore } = useRootStore()
  const { detailTrack } = matchupStore
  const { matchId } = detailTrack?.track ?? ({} as MatchTrack)
  const { sportTalkList } = cheerForTeamStore
  const { sportIssuePreviewList, sportTalkPickUserInfos } = componentStore
  const [editorData, setEditorData] = useState({
    isNew: true,
    content: '',
    attachment: null,
    attachmentType: CHEER_FOR_TEAM_CONTENTS_TYPE.TEXT_ONLY,
    attachmentDetail: null,
    cheerForTeamData: null,
  })
  const disabledMatchTimeline = useRef(false)

  // 스크롤 이벤트 발생 시 다른 컨텍스트에서도 참조할 수 있도록 저장한다.
  const cheerForTeamStoreRef = useRef<CheerForTeamStore>(cheerForTeamStore)
  const matchupStoreRef = useRef<MatchupStore>(matchupStore)

  const isVisibleDashboard = _.size(sportIssuePreviewList) > 0 || _.size(sportTalkPickUserInfos) > 0

  const listScrollHandler = () => {
    if (!cheerForTeamStoreRef.current.hasMoreCheerForTeam || cheerForTeamStoreRef.current.isFetchCheerForTeamLoading) {
      return
    }

    const matchDetailWrap = document.getElementById('matchDetailWrap')
    // 전체 높이 (scrollHeight) - 화면 높이 (clientHeight) - 마지막 화면에 스크롤 지점 (화면 높이/2) < 현재 스크롤 위치
    // 전체 높이 - 화면 높이 = 최대 scrollTop 에 근접
    // 전체 높이 - 화면 높이 - (화면 높이/2) 는 스크롤이 거의 모두 내려가서 현재 화면 높이의 반 보다 적게 스크롤 할 양이 남았을 경우를 의미한다.
    if (matchDetailWrap.scrollHeight - matchDetailWrap.clientHeight - (matchDetailWrap.clientHeight / 2)
      < matchDetailWrap.scrollTop) {
      endReachedHandler()
    }
  }

  // 빠르게 여러번 발생하는 이벤트는 스로틀로 0.2초에 한번만 처리한다.
  const throttleListScrollHandler = _.throttle(listScrollHandler, 200)

  let matchDetailWrap
  // 스크롤 이벤트 해제 시 dom 이 이미 제거되었을 수 있으므로 useOnUnmount 보다 componentWillUnmount 과 유사한 useEffect return 을 이용한다.
  useEffect(() => {
    matchDetailWrap = document.getElementById('matchDetailWrap')
    matchDetailWrap.addEventListener('scroll', throttleListScrollHandler, true)

    return () => {
      matchDetailWrap.removeEventListener('scroll', throttleListScrollHandler, true)
    }
  }, [])

  useEffect(() => {
    if (_.isNil(matchId)) {
      return
    }

    resetEditorData()
    fetchSportTalk(matchId, true).catch(console.error)
    fetchDashboard(matchId).catch(console.error)
  }, [matchId])

  const endReachedHandler = async () => {
    const { detailTrack } = matchupStoreRef.current
    const { matchId } = detailTrack?.track ?? ({} as MatchTrack)

    const { hasMoreCheerForTeam } = cheerForTeamStoreRef.current
    if (hasMoreCheerForTeam && !cheerForTeamStoreRef.current.isFetchCheerForTeamLoading) {
      cheerForTeamStoreRef.current.fetchCheerForTeam(matchId, false, disabledMatchTimeline.current).catch(console.error)
    }
  }

  const resetEditorData = () => {
    setEditorData({
      isNew: true,
      content: '',
      attachment: null,
      attachmentType: CHEER_FOR_TEAM_CONTENTS_TYPE.TEXT_ONLY,
      attachmentDetail: null,
      cheerForTeamData: null,
    })
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  })

  const fetchSportTalk = async (matchId: number, reset = true) => {
    if (reset) {
      const timelineEnabled = globalStore.getSportTalkMatchTimelineEnabled()
      disabledMatchTimeline.current = !timelineEnabled
    }

    await cheerForTeamStore.fetchCheerForTeam(matchId, reset, disabledMatchTimeline.current)
  }

  const fetchDashboard = async (matchId: number) => {
    await componentStore.fetchSportDashboardList(matchId)
  }

  const onSave = async (pickEditorData: AdvEditorData): Promise<boolean> => {
    const effectEnabled = isNotStartedMatchStatus(pickEditorData.cheerForTeamData.matchStatus)
      ? false : cheerForTeamStore.effectEnabled
    clearTimeout(saveCheerForTeamTimer)
    saveCheerForTeamTimer = setTimeout(async () => {
      const params = {
        contents: pickEditorData.content,
        attachmentType: pickEditorData.attachmentType as CHEER_FOR_TEAM_CONTENTS_TYPE,
        attachment: pickEditorData.attachment,
        attachmentDetail: pickEditorData.attachmentDetail,
        cheerForTeamData: pickEditorData.cheerForTeamData,
        effectEnabled,
      }

      const result = await cheerForTeamStore.api.cheerForTeam.createCheerForTeam(params)
      matchupStore.updateMatchCheerForTeamWritten(pickEditorData.cheerForTeamData.matchId)
      fetchSportTalk(matchId, true).then(() => {
        globalStore.addActivityScoreNoticeQueue(result.activityInfo)
      }).catch(console.error)
    }, 500)
    return true
  }

  const makeCheerForTeamData = async (): Promise<void> => {
    const refreshedCheerForTeamData = await chattingStore.getCheerForTeamMatchInfo(matchId)
    const defaultEffectIcon = ImageUtils.getDefaultCheerForTeamEffectIcon()
    refreshedCheerForTeamData.effectIconUrl = defaultEffectIcon.img
    editorData['cheerForTeamData'] = refreshedCheerForTeamData
    setEditorData(_.cloneDeep(editorData))
  }

  let beforePlaceHolder
  if (!authStore.isSignedIn) {
    beforePlaceHolder = '로그인 후 이용할 수 있습니다.'
  } else {
    beforePlaceHolder = '스포츠톡을 입력해보세요.'
  }

  const onOpen = async (): Promise<void> => {
    if (authStore.isSignedIn) {
      makeCheerForTeamData().catch(console.error)
    }
  }

  const onClickRefreshSportTalkList = () => {
    clearTimeout(refreshSportTalkTimer)
    refreshSportTalkTimer = setTimeout(async () => {
      cheerForTeamStore.fetchCheerForTeam(matchId, true).catch(console.error)
      matchupDetailScrollToTop()
    }, 500)
  }

  const renderHeader = (): React.ReactElement => {
    const showCheerForTeam = !_.isNil(editorData.cheerForTeamData?.matchId)
      && !isNotStartedMatchStatus(editorData.cheerForTeamData?.matchStatus)

    return (
      <Fragment>
        {isVisibleDashboard && <SportIssueList matchId={matchId}/>}
        <AdvComments className="sport-talk-editor">
          <div className="sport-talk-tit-wrap">
            <p className="sport-talk-tit-txt">스포츠톡</p>
            <div className="sport-talk-refresh-btn">
              <AdvButton
                flex
                alignCenter
                width={45}
                height={45}
                borderRadius={'50%'}
                ico={<PageResetIco/>}
                className="write-btn"
                onClick={onClickRefreshSportTalkList}
              />
            </div>
          </div>
          <AdvEditor
            showCheerForTeam={showCheerForTeam}
            showBettingHistory={true}
            isFocus={false}
            beforePlaceholder={beforePlaceHolder}
            placeholder={`스포츠톡을 입력해보세요.\n*도배, 무의미한 글은 제약이 발생할 수 있습니다.`}
            callbackOpen={onOpen}
            callbackClose={null}
            callbackSave={onSave}
            minLength={0}
            maxLength={200}
            maxLine={100}
            fromViewType={FROM_VIEW_TYPE.MATCHUP_DETAIL_CHEER_FOR_TEAM}
            editorData={editorData}
          />
        </AdvComments>
      </Fragment>
    )
  }

  const renderRow = (
    item: Schema$SportTalkListItem,
  ): React.ReactElement => {
    const { sportTalkItemType } = item

    if (sportTalkItemType === SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM) {
      return (
        <SportTalkItem
          sportTalk={item.cheerForTeam}
          fetchCheerForTeams={() => {
            fetchSportTalk(matchId, true).catch(console.error)
          }}
        />
      )
    } else if (sportTalkItemType === SPORT_TALK_ITEM_TYPE.MATCH_TIMELINE) {
      return (<AdvTimelineItem advTimeline={item.matchTimeline}/>)
    }

    return <Fragment/>
  }

  const renderFooter = () => {
    let footer
    if (_.size(sportTalkList) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.SPORT_TALK_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const matchUpDetailSportTalkClassName = classNames(
    '',
    { 'pc': !isMobile },
    { 'is-game-info-match-chart': props.isGameInfoMatchChart },
  )

  return (
    <MatchUpDetailSportTalkWrap className={matchUpDetailSportTalkClassName}>
      {
        _.isNil(matchId) ?
          <AdvEmpty ico={EMPTY_ICON.MATCHUP_DETAIL_MATCH}/>
          :
          <ListView
            css={ListViewStyle}
            dataSource={dataSource.cloneWithRows(sportTalkList ?? [])}
            renderHeader={renderHeader}
            renderRow={renderRow}
            renderFooter={renderFooter}
            initialListSize={_.size(sportTalkList)}
            pageSize={_.size(sportTalkList)}
          />
      }
    </MatchUpDetailSportTalkWrap>
  )
})
